import { getPublicApiBaseUrl } from '../../utils/publicApiBaseUrl';

/** Avoid SyntaxError when the API returns HTML/plain text (proxy errors, CORS pages, etc.) */
async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  let text: string;
  try {
    text = await response.text();
  } catch {
    return `${fallback} (HTTP ${response.status})`;
  }
  const trimmed = text.trim();
  if (!trimmed) {
    return `${fallback} (HTTP ${response.status})`;
  }
  try {
    const parsed = JSON.parse(trimmed) as { error?: string };
    if (typeof parsed.error === 'string' && parsed.error.trim()) {
      return parsed.error.trim();
    }
    return `${fallback} (HTTP ${response.status})`;
  } catch {
    /* not JSON — use a short snippet (e.g. HTML error page) */
  }
  return trimmed.length <= 200 ? trimmed : `${trimmed.slice(0, 200)}…`;
}

export const formsApi = {
  // Submit a form (public, no auth required)
  async submitForm(
    pageId: string,
    componentId: string,
    formData: Record<string, any>
  ): Promise<{ success: boolean; message: string; redirectUrl?: string }> {
    const base = getPublicApiBaseUrl();
    const response = await fetch(`${base}/forms/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pageId,
        componentId,
        formData,
      }),
    });

    if (!response.ok) {
      throw new Error(await readErrorMessage(response, 'Failed to submit form'));
    }

    return await response.json();
  },

  // Get form configuration (public, no auth required)
  async getFormConfig(pageId: string, componentId: string): Promise<any> {
    const base = getPublicApiBaseUrl();
    const response = await fetch(`${base}/forms/${pageId}/${componentId}/config`);

    if (!response.ok) {
      throw new Error(await readErrorMessage(response, 'Failed to get form configuration'));
    }

    return await response.json();
  },
};

export const healthApi = {
  async check(): Promise<{ status: string; timestamp: string }> {
    const { default: apiClient } = await import('../core');
    const response = await apiClient.get('/health');
    return response.data;
  },
};
